import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Phone, Hash, CalendarDays, Globe, ChevronRight, Smartphone, Tablet, Monitor, MonitorSmartphone, Languages, ShoppingCart, Copy, Check } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { conversationsApi } from "../services/conversationsApi";
import { formatAbsolute, formatRelative } from "../utils/datetime";
import { markdownToPlainText } from "../utils/markdown";
import {
  CART_ARTICLES_SHOWN,
  MAX_CART_ARTICLES,
  MAX_NESTED_ENTRIES,
  baseLanguage,
  clipText,
  collapsedCount,
  PROFILE_NAME_KEY,
  humanizeKey,
  isLanguageTag,
  isScalar,
  languageName,
  latestClient,
  latestContextAttributes,
  latestCustomerProfile,
  nestedEntries,
  orderProfileEntries,
  parseCart,
  parsePageUrl,
  isMoney,
  withVersion,
} from "../utils/contextAttributes";
import ConversationStatusDot, { STATUS_KEYS, statusHasDot } from "./ConversationStatusDot";
import { COLUMN_HEADER_CLASS } from "./conversationLayout";
import Skeleton from "./Skeleton";

// Dez chegam para dizer se o contacto ja ca esteve, que e a pergunta que este
// painel responde. Quando ha mais do que estas dez, a seccao di-lo: a lista da
// esquerda NAO filtra por contacto, portanto nao ha outro sitio no backoffice
// onde o operador as va buscar, e calar isso fazia as dez parecerem tudo.
const HISTORY_SHOWN = 10;

// Pedem-se duas a mais do que se mostra. Uma para saber que ha mais do que cabe.
// A outra porque a conversa aberta e sempre uma das deste contacto e sai da
// lista aqui em baixo: com so uma a mais, um contacto com doze mostrava dez sem
// dizer que havia mais. Menos items do que este limite significa que veio tudo.
const HISTORY_FETCH = HISTORY_SHOWN + 2;

// A moldura da seccao do historico. Partilhada pelo esqueleto e pelo painel
// carregado: e o que garante que o titulo aterra no mesmo sitio nos dois.
const HISTORY_SECTION_CLASS = "mt-3 pt-3 border-t border-[var(--color-border)]";

// O painel e contexto, nao e o conteudo: nao faz polling. Estes dados nao se
// mexem enquanto o operador le a transcricao, e um painel lateral a piscar ao
// lado do texto que ele esta a ler custa mais do que a atualizacao vale.

const LINK_CLASSES =
  "underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]";

// RPAA-286: o dispositivo de uma mensagem do cliente, como o backend o classificou.
// Valores de um conjunto fechado; `other` e o que ele nao soube nomear, e leva um icone
// neutro: um computador ao lado de "Outro dispositivo" diria o que nao se sabe.
const DEVICE_ICONS = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };
const DEVICE_LABELS = {
  mobile: "conversationsDeviceMobile",
  tablet: "conversationsDeviceTablet",
  desktop: "conversationsDeviceDesktop",
  other: "conversationsDeviceOther",
};

const deviceIcon = (client) => DEVICE_ICONS[client.device] || MonitorSmartphone;

// "Telemovel · iOS 26 · Safari 26". Um sistema ou um browser `other` fica de fora em
// vez de se ler como um nome, e uma versao que o browser congelou chega a null e nao
// se adivinha.
const deviceSummary = (client, translate) =>
  [
    translate(DEVICE_LABELS[client.device] || DEVICE_LABELS.other),
    client.os !== "other" && withVersion(client.os, client.os_version),
    client.browser !== "other" && withVersion(client.browser, client.browser_version),
  ]
    .filter(Boolean)
    .join(" · ");

// O email e o telefone do contacto sao texto livre no Chatwoot. Postos crus num
// `mailto:`, um valor com `?cc=&subject=&body=` preenche o compositor de mail do
// operador com o que o autor quiser, e um esquema qualquer ali era pior. Sao os
// dois um `href` na sessao autenticada do operador, portanto levam o mesmo
// tratamento que o URL da pagina e que o destino do cartao: o que nao passa
// desenha-se como texto simples, que nao clica em lado nenhum.
//
// Deliberadamente estreitos, e nao uma gramatica de email: o que interessa e nao
// deixar passar o que muda o SIGNIFICADO do URI, ou seja `?`, `&`, `#`, `%`,
// espacos e os separadores de destinatarios (`,` e `;`).
const SAFE_EMAIL = /^[^\s@,;:<>"'()[\]\\?&#%]+@[^\s@,;:<>"'()[\]\\?&#%]+\.[A-Za-z]{2,}$/;

// Digitos, com o `+` internacional e os separadores que as pessoas escrevem. Os
// separadores saem do href: um `tel:` nao os leva, so o numero.
const SAFE_PHONE = /^\+?[\d\s().-]{3,30}$/;

const mailtoHref = (email) =>
  typeof email === "string" && SAFE_EMAIL.test(email) ? `mailto:${email}` : null;

const telHref = (phone) =>
  typeof phone === "string" && SAFE_PHONE.test(phone)
    ? `tel:${phone.replace(/[\s().-]/g, "")}`
    : null;

// Link quando o valor passou, o valor a seco quando nao passou.
const MaybeLink = ({ href, children }) =>
  href ? (
    <a href={href} className={LINK_CLASSES}>
      {children}
    </a>
  ) : (
    <>{children}</>
  );

// Um facto curto de identidade: o icone e o rotulo visivel, a palavra fica em
// sr-only para o leitor de ecra. E assim que qualquer cartao de contacto se le,
// e poupa uma linha cinzenta por cima de cada valor.
//
// `wrap` e para o que nao pode ser cortado (a pagina, as linguas): o icone fica na
// primeira linha e o texto parte em vez de levar reticencias.
const IconRow = ({ icon: Icon, label, children, title, wrap }) => (
  <div className={wrap ? "flex items-start gap-3 min-h-[32px] py-[7px]" : "flex items-center gap-3 min-h-[32px]"}>
    <Icon
      size={16}
      className={wrap ? "shrink-0 mt-px" : "shrink-0"}
      style={{ color: "var(--color-text-secondary)" }}
      aria-hidden="true"
    />
    <dt className="sr-only">{label}</dt>
    <dd
      className={wrap ? "min-w-0 flex-1 text-[13px] leading-[18px] break-words" : "min-w-0 flex-1 text-[13px] leading-[18px] truncate"}
      style={{ color: "var(--color-text-primary)" }}
      title={title}
    >
      {children}
    </dd>
  </div>
);

// Par rotulo/valor numa linha de duas colunas, para o que e livre (atributos do
// tenant, perfil): sao chaves que o site manda e nao ha icone que as represente.
// Os factos da plataforma (pagina, dispositivo, linguas, carrinho) levam icone.
// O muted nunca serve para texto: da 2.54:1 sobre branco.
const PairRow = ({ label, children }) => (
  <div className="grid grid-cols-[40%_1fr] gap-2 items-start">
    <dt className="text-xs leading-[18px] break-words" style={{ color: "var(--color-text-secondary)" }}>
      {label}
    </dt>
    <dd className="text-[13px] leading-[18px] break-words min-w-0" style={{ color: "var(--color-text-primary)" }}>
      {children}
    </dd>
  </div>
);

const collapsedLabel = (value, translate) => {
  const { isList, count } = collapsedCount(value);
  const key = isList
    ? count === 1
      ? "conversationsContextItemsOne"
      : "conversationsContextItems"
    : count === 1
    ? "conversationsContextFieldsOne"
    : "conversationsContextFields";
  return translate(key, { count });
};

// Uma contagem nao e um valor, e a descricao de um valor que nao coube. Fica no
// secundario de proposito, para nao se ler como um conteudo.
const CollapsedValue = ({ value, translate }) => (
  <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
    {collapsedLabel(value, translate)}
  </span>
);

const ScalarValue = ({ value, translate }) => {
  // Um booleano cru pergunta ao operador o que e `true`. Sim e Nao ja e uma
  // resposta, e passa pelo translate como todo o resto do texto.
  if (typeof value === "boolean") return <>{translate(value ? "yes" : "no")}</>;
  const { shown, title } = clipText(value);
  return <span title={title}>{shown}</span>;
};

// Um nivel de encaixe, numa linha so ("Jeans 28 · Sapatos 38"): os escalares a
// mostra e o que estiver mais fundo reduzido a uma contagem.
const NestedValue = ({ value, translate }) => {
  const entries = nestedEntries(value);
  if (!entries.length) return <CollapsedValue value={value} translate={translate} />;
  const shown = entries.slice(0, MAX_NESTED_ENTRIES);
  const rest = entries.length - shown.length;
  // Cada parte corta-se como qualquer valor; se alguma foi cortada, o title leva
  // o texto inteiro, que e onde o operador o vai buscar.
  let clipped = false;
  const parts = shown.map(([key, nested]) => {
    let text;
    let full;
    if (!isScalar(nested)) {
      text = collapsedLabel(nested, translate);
      full = text;
    } else if (typeof nested === "boolean") {
      text = translate(nested ? "yes" : "no");
      full = text;
    } else {
      const clip = clipText(nested);
      text = clip.shown;
      full = String(nested);
      if (clip.title !== undefined) clipped = true;
    }
    return { text: `${humanizeKey(key)} ${text}`, full: `${humanizeKey(key)} ${full}` };
  });
  if (rest > 0) {
    const more = translate("conversationsContextMore", { count: rest });
    parts.push({ text: more, full: more });
  }
  return <span title={clipped ? parts.map((p) => p.full).join(" · ") : undefined}>{parts.map((p) => p.text).join(" · ")}</span>;
};

// So as chaves da plataforma (current_page_url, page_language, cart) se tratam a
// mao. Tudo o resto passa pelo caminho generico, para que uma chave nova do
// tenant apareca no painel sem ninguem mexer aqui.
const ContextValue = ({ value, translate }) => {
  if (isScalar(value)) return <ScalarValue value={value} translate={translate} />;
  return <NestedValue value={value} translate={translate} />;
};

// Copiar um valor tecnico. Se a escrita no clipboard falhar nao acontece nada
// visivel, o valor continua ali para se selecionar a mao. Fora de um contexto
// seguro nao ha `navigator.clipboard`: nao se faz nada.
const CopyButton = ({ value, label, translate }) => {
  const [copied, setCopied] = useState(false);
  const mounted = useRef(true);
  // Volta a true na montagem: o StrictMode corre a limpeza e o efeito outra vez em
  // desenvolvimento, e sem isto o ref ficava false e o "Copiado" nunca aparecia.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(
      () => {
        if (mounted.current) setCopied(true);
      },
      () => {}
    );
  };
  const CopyIcon = copied ? Check : Copy;
  return (
    <>
      <button
        type="button"
        onClick={copy}
        aria-label={label}
        className="shrink-0 p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
      >
        <CopyIcon size={14} aria-hidden="true" />
      </button>
      {/* A regiao existe sempre, senao o leitor de ecra nao anuncia o que
          aparece nela. */}
      <span role="status" className="sr-only">
        {copied ? translate("conversationsContextCopied") : ""}
      </span>
    </>
  );
};

// Rotulo pequeno por cima de um valor tecnico, o mesmo para o IP e para o
// User-Agent, para os dois se lerem como pares.
const TechnicalValue = ({ label, value, copyLabel, translate }) => (
  <div>
    <div>{label}</div>
    <div className="flex items-start gap-2 mt-0.5">
      <code className="min-w-0 flex-1 font-mono text-[11px] leading-4 break-words" style={{ color: "var(--color-text-primary)" }}>
        {value}
      </code>
      <CopyButton value={value} label={copyLabel} translate={translate} />
    </div>
  </div>
);

// O User-Agent e o IP sao os dados menos uteis para quem atende, portanto ficam
// recolhidos. Sem o corte de 100 caracteres: quem abre quer o valor todo. O IP
// vem primeiro por ser o mais curto e o mais pedido.
const TechnicalDetails = ({ ip, userAgent, translate }) => (
  <details className="group text-xs" style={{ color: "var(--color-text-secondary)" }}>
    {/* O marcador nativo sai e entra um chevron que roda com o estado aberto:
        o mesmo gesto do resto do painel, e continua a ser um summary, por isso
        o teclado funciona. */}
    <summary className="flex items-center gap-1 list-none [&::-webkit-details-marker]:hidden cursor-pointer font-medium rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]">
      <ChevronRight size={14} className="shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
      {translate("conversationsContextTechnical")}
    </summary>
    <div className="mt-1.5 space-y-2">
      {ip && (
        <TechnicalValue
          label={translate("conversationsContextIp")}
          value={ip}
          copyLabel={translate("conversationsContextCopyIp")}
          translate={translate}
        />
      )}
      {userAgent && (
        <TechnicalValue
          label={translate("conversationsContextUserAgentLabel")}
          value={userAgent}
          copyLabel={translate("conversationsContextCopyUserAgent")}
          translate={translate}
        />
      )}
    </div>
  </details>
);

// A diferenca entre a lingua da pagina e a do browser e o sinal util (navega em
// portugues com o browser em ingles), por isso vao juntas. Com a mesma lingua base
// a do browser chega, que e a mais especifica. Os codigos crus vao no title, para
// quem precisar deles.

const languagesSummary = (pageLanguage, browserLanguage, language, translate) => {
  const page = pageLanguage && languageName(pageLanguage, language);
  const browser = browserLanguage && languageName(browserLanguage, language);
  const pageLine = page && translate("conversationsContextPageLanguage", { language: page });
  const browserLine = browser && translate("conversationsContextBrowserLanguage", { language: browser });
  let lines;
  if (page && browser) {
    // Duas linhas e nao um " · ": a 20rem a segunda metade era cortada, e e ela
    // que faz a linha valer a pena.
    lines =
      baseLanguage(pageLanguage) === baseLanguage(browserLanguage)
        ? [browser.charAt(0).toUpperCase() + browser.slice(1)]
        : [pageLine, browserLine];
  } else {
    lines = [pageLine || browserLine];
  }
  return { lines, title: [pageLanguage, browserLanguage].filter(Boolean).join(" · ") };
};

// O carrinho nao traz moeda, e o backend assume euros ao desenha-lo para o agente
// (`_addon_labels` em core/prompt_config.py): o painel diz o mesmo que o agente le.
const formatMoney = (amount, language) =>
  `${new Intl.NumberFormat(language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} €`;

// Fora do carrinho de quem comprou: o que ainda se podia oferecer, a mesma lista
// que o agente le (utils/contextAttributes.js). Texto simples, nada vira link.
const toOfferLabel = (addons, language) =>
  addons.map((addon) => (isMoney(addon.price) ? `${addon.name} ${formatMoney(addon.price, language)}` : addon.name)).join(" · ");

// O mesmo texto com os nomes inteiros, para o title: so quando algum nome foi cortado.
const toOfferTitle = (addons, language) =>
  addons.some((addon) => addon.title)
    ? toOfferLabel(addons.map((addon) => ({ ...addon, name: addon.title ?? addon.name })), language)
    : undefined;

// Uma linha sem nome (o /carrinho/ da RP manda so id e preco): o backend vai buscar
// o nome ao catalogo, o painel nao o tem e diz qual e a linha.
// O id e do visitante como o resto do carrinho: corta-se, e o title leva o inteiro.
const articleName = (item, translate) => {
  if (item.name) return { text: item.name, title: item.nameTitle };
  const { shown, title } = clipText(item.id);
  const text = translate("conversationsCartItemFallback", { id: shown }).trim();
  return { text, title: title === undefined ? undefined : translate("conversationsCartItemFallback", { id: title }).trim() };
};

// O widget manda o carrinho como texto JSON, e o painel mostrava-o cru e cortado.
// Sempre aberto, sem triangulo: o resumo (quantos artigos e quanto) e as linhas por
// baixo, na coluna do texto. O preco de uma linha e o total DA LINHA.
const CartRow = ({ cart, translate, language }) => {
  const [expanded, setExpanded] = useState(false);
  // O botao "Mostrar os outros" desmonta-se no clique: sem isto o foco caia no body.
  // Vai para o primeiro artigo revelado.
  const firstRevealed = useRef(null);
  useEffect(() => {
    if (expanded) firstRevealed.current?.focus();
  }, [expanded]);
  const visible = expanded ? MAX_CART_ARTICLES : CART_ARTICLES_SHOWN;
  const rowClass = "flex items-start gap-3 min-h-[32px] py-[7px]";
  const icon = <ShoppingCart size={16} className="shrink-0 mt-px" style={{ color: "var(--color-text-secondary)" }} aria-hidden="true" />;
  const label = <dt className="sr-only">{translate("conversationsCart")}</dt>;
  if (cart.articles.length === 0) {
    return (
      <div className={rowClass}>
        {icon}
        {label}
        <dd className="min-w-0 flex-1 text-[13px] leading-[18px]" style={{ color: "var(--color-text-primary)" }}>
          {translate("conversationsCartEmpty")}
        </dd>
      </div>
    );
  }
  const count = translate(cart.count === 1 ? "conversationsCartItemsOne" : "conversationsCartItems", { count: cart.count });
  const summary = cart.total === undefined ? count : `${count} · ${formatMoney(cart.total, language)}`;
  return (
    <div className={rowClass}>
      {icon}
      {label}
      <dd className="min-w-0 flex-1 text-[13px] leading-[18px]" style={{ color: "var(--color-text-primary)" }}>
        {summary}
        <ul className="mt-1.5 space-y-1.5">
          {cart.articles.slice(0, visible).map((item, i) => {
            const name = articleName(item, translate);
            return (
            <li
              key={`${item.id}-${i}`}
              ref={i === CART_ARTICLES_SHOWN ? firstRevealed : undefined}
              tabIndex={i === CART_ARTICLES_SHOWN ? -1 : undefined}
              className="focus:outline-none"
            >
              <div className="flex gap-2 justify-between">
                <span className="min-w-0 break-words" title={name.title}>{name.text}</span>
                {isMoney(item.price) ? (
                  <span className="shrink-0">{formatMoney(item.price, language)}</span>
                ) : (
                  item.priceText && (
                    <span className="shrink-0 max-w-[50%] break-words" title={item.priceTitle}>
                      {item.priceText}
                    </span>
                  )
                )}
              </div>
              {item.qty > 1 && (
                <div className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("conversationsCartUnits", { qty: item.qty })}
                </div>
              )}
              {item.chosen.map((addon, j) => (
                <div key={j} className="flex gap-2 justify-between text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  <span className="min-w-0 break-words" title={addon.title}>{`+ ${addon.name}`}</span>
                  {isMoney(addon.price) && <span className="shrink-0">{formatMoney(addon.price, language)}</span>}
                </div>
              ))}
              {item.toOffer.length > 0 && (
                <div
                  className="text-[11px] leading-4 mt-0.5"
                  style={{ color: "var(--color-text-secondary)" }}
                  title={toOfferTitle(item.toOffer, language)}
                >
                  {translate("conversationsCartToOffer", { list: toOfferLabel(item.toOffer, language) })}
                </div>
              )}
            </li>
            );
          })}
          {!expanded && cart.articles.length > CART_ARTICLES_SHOWN && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="text-xs underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
                style={{ color: "var(--color-text-secondary)" }}
              >
                {translate("conversationsCartShowOthers", {
                  count: Math.min(cart.articles.length, MAX_CART_ARTICLES) - CART_ARTICLES_SHOWN,
                })}
              </button>
            </li>
          )}
          {cart.articles.length > MAX_CART_ARTICLES && visible === MAX_CART_ARTICLES && (
            <li className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
              {translate("conversationsContextMore", { count: cart.articles.length - MAX_CART_ARTICLES })}
            </li>
          )}
        </ul>
      </dd>
    </div>
  );
};

// Titulo de seccao: 12px, peso medio, secundario, caixa normal. Nao e um
// "eyebrow" em maiusculas: e o nome de uma seccao.
const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-medium leading-4 mb-2" style={{ color: "var(--color-text-secondary)" }}>
    {children}
  </h3>
);

// As medidas sao as do `historyRow` la em baixo: py-2, uma caixa de 16px para a
// data e o estado, 2px, e uma caixa de 18px para o preview, com o disco do
// estado e a seta. Tres linhas porque o painel mostra no maximo dez mas quase
// sempre poucas, e um esqueleto mais alto do que o conteudo encolhe quando os
// dados chegam.
const HISTORY_SKELETON_PREVIEWS = ["w-4/5", "w-2/3", "w-3/4"];

const HistoryRowsSkeleton = () => (
  <div className="-mx-2">
    {HISTORY_SKELETON_PREVIEWS.map((preview, i) => (
      <div key={i} className="flex items-center gap-3 px-2 py-2">
        <div className="min-w-0 flex-1">
          <div className="h-4 flex items-center">
            <Skeleton variant="custom" width="w-28" height="h-3" rounded="rounded" />
          </div>
          <div className="h-[18px] mt-0.5 flex items-center">
            <Skeleton variant="custom" width={preview} height="h-3" rounded="rounded" />
          </div>
        </div>
        <Skeleton variant="custom" width="w-4" height="h-4" rounded="rounded" />
      </div>
    ))}
  </div>
);

// O `conversation` chega de cima, ja carregado pela transcricao. O painel nao
// o vai buscar outra vez de proposito: o detalhe traz a transcricao inteira, e
// pedi-lo duas vezes por conversa aberta era puxar todas as mensagens em
// duplicado para chegar a um objeto de contacto.
//
// A concha decide se a coluna existe (e o operador que a abre e fecha). Este
// componente so decide o que vai dentro.
const ConversationContactPanel = ({ conversationId, conversation, loading }) => {
  const { translate, language } = useLanguage();
  const navigate = useNavigate();
  // null = ainda nao sabemos. {rows: [], more: false} = sabemos que nao ha
  // nenhuma. A distincao e o que impede o painel de afirmar "primeira conversa
  // deste contacto" quando na verdade foi a leitura que falhou.
  const [history, setHistory] = useState(null);
  // O null tambem significaria "a ler" para o esqueleto, e uma leitura que falha
  // nunca mais sai desse estado. Esta segunda bandeira e o que deixa a seccao
  // dizer que nao conseguiu saber, como a lista e a transcricao ja fazem, em vez
  // de desaparecer: uma seccao que some le-se como "este contacto nao tem
  // historico", que e uma afirmacao que ninguem verificou.
  const [historyFailed, setHistoryFailed] = useState(false);

  const contactId = conversation?.contact?.id || null;

  useEffect(() => {
    if (!contactId) return undefined;
    let cancelled = false;
    setHistory(null);
    setHistoryFailed(false);
    conversationsApi
      .list({ contactId, limit: HISTORY_FETCH })
      // A lista ja vem ordenada do backend, a mesma ordem da coluna da esquerda.
      // Era por created_at DESC ate ao PR #246 do backend, e e pela ultima
      // atividade desde entao; o que importa aqui e continuarem a ser a mesma, e
      // por isso nao se reordena nada deste lado. A conversa aberta sai fora: ela
      // e o ecra, nao e historico.
      .then((data) => {
        // Filtrar pelo contact_id que veio na resposta, e nao confiar que o
        // servidor honrou o filtro: o FastAPI ignora em silencio um parametro
        // de query que nao declara, e nesse caso isto devolvia as conversas
        // todas do tenant debaixo do titulo "conversas anteriores deste
        // contacto". Verificado, nao e hipotetico. Com a verificacao aqui, um
        // servidor que ignore o filtro produz uma lista vazia em vez de uma
        // lista errada.
        if (cancelled) return;
        const mine = data.items.filter(
          (c) => c.contact_id === contactId && c.id !== conversationId
        );
        setHistory({
          rows: mine.slice(0, HISTORY_SHOWN),
          more: mine.length > HISTORY_SHOWN,
        });
      })
      .catch((error) => {
        if (!cancelled) setHistoryFailed(true);
        console.error("contact history failed", error);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, conversationId]);

  // Se a transcricao falhou, a coluna desaparece e a transcricao fica com o
  // espaco, que e o que faz sentido ao lado de um erro.
  if (!conversation && !loading) return null;

  const statusLabel = (s) => (STATUS_KEYS[s] ? translate(STATUS_KEYS[s]) : s);

  const historyRow = (c) => {
    const when = c.last_message_at || c.created_at;
    const DeviceIcon = c.client ? deviceIcon(c.client) : null;
    return (
      <button
        key={c.id}
        type="button"
        onClick={() => navigate(`/conversations/${c.id}`)}
        className="w-full text-left flex items-center gap-3 px-2 py-2 rounded-lg transition-colors duration-[120ms] hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 h-4 text-xs" style={{ color: "var(--color-text-secondary)" }}>
            <span title={formatAbsolute(when, language)}>{formatRelative(when, language)}</span>
            <span className="inline-flex items-center gap-1.5">
              {statusHasDot(c.status) && <ConversationStatusDot status={c.status} />}
              {statusLabel(c.status)}
            </span>
            {c.client && (
              // So o sistema: a linha tem 20rem e ja leva a data e o estado. O resto
              // fica no title.
              <span className="inline-flex items-center gap-1 min-w-0 truncate" title={deviceSummary(c.client, translate)}>
                <DeviceIcon size={12} className="shrink-0" aria-hidden="true" />
                {/* O icone e aria-hidden e o texto so diz o sistema: o leitor de
                    ecra ouve o tipo de dispositivo daqui (sem sistema, o texto visivel ja o diz). */}
                {c.client.os !== "other" && (
                  <span className="sr-only">{translate(DEVICE_LABELS[c.client.device] || DEVICE_LABELS.other)}</span>
                )}
                {/* O texto leva o seu proprio truncate: o contentor e flex, e
                    as reticencias so se desenham num bloco de texto. */}
                <span className="truncate">
                  {c.client.os !== "other"
                    ? withVersion(c.client.os, c.client.os_version)
                    : translate(DEVICE_LABELS[c.client.device] || DEVICE_LABELS.other)}
                </span>
              </span>
            )}
          </span>
          {/* O preview chega em markdown. Sem isto o operador le `**329,99€**`. */}
          <span className="block text-[13px] leading-[18px] truncate mt-0.5" style={{ color: "var(--color-text-primary)" }}>
            {c.preview ? markdownToPlainText(c.preview) : translate("conversationsNoMessages")}
          </span>
        </span>
        {/* A seta e o que dizia falta as linhas antigas: que se pode clicar. */}
        <ChevronRight size={16} className="shrink-0" style={{ color: "var(--color-text-secondary)" }} aria-hidden="true" />
      </button>
    );
  };

  const contact = conversation?.contact || null;
  // Sem bloco de contexto na conversa a seccao nao existe, nem sequer o titulo.
  // Todas as conversas gravadas antes de o backend passar a escrever este bloco
  // caem aqui, e um cabecalho vazio em cima delas so ensinava que falta ali
  // qualquer coisa.
  const contextAttributes = conversation ? latestContextAttributes(conversation) : null;
  // KAN-41: o que o agente sabia sobre o cliente (perfil do CDP e, na MODZ, os
  // tamanhos do cookie). Sem perfil nao ha seccao, pelo mesmo motivo do contexto.
  const customerProfile = conversation ? latestCustomerProfile(conversation) : null;
  // RPAA-286: o dispositivo da mensagem mais recente que o tenha. Vai para a seccao do
  // contexto, porque e tambem o que o browser do cliente disse naquele turno.
  const client = conversation ? latestClient(conversation) : null;
  // A pagina, a lingua da pagina e o carrinho sao chaves da propria plataforma e
  // desenham-se a mao, em cima; o resto do bloco e dos tenants e continua generico,
  // para uma chave nova aparecer sem mexer aqui.
  const entryValue = (key) => (contextAttributes || []).find(([k]) => k === key)?.[1];
  const pageUrl = entryValue("current_page_url");
  const page = pageUrl === undefined ? null : parsePageUrl(pageUrl, conversation.store_origins);
  // So uma `page_language` com forma de tag e uma lingua; outra (o visitante escreve-a)
  // fica na lista generica, onde se corta como qualquer valor.
  const pageLanguage = isLanguageTag(entryValue("page_language")) ? entryValue("page_language") : null;
  // Um carrinho que nao se le como carrinho fica na lista generica, como o valor cru.
  const cart = parseCart(entryValue("cart"));
  const tenantAttributes = (contextAttributes || []).filter(
    ([key]) => key !== "current_page_url" && !(key === "page_language" && pageLanguage) && !(key === "cart" && cart)
  );
  const browserLanguage = isLanguageTag(client?.language) ? client.language : null;
  const languages =
    pageLanguage || browserLanguage
      ? languagesSummary(pageLanguage, browserLanguage, language, translate)
      : null;
  // A seccao so existe se se desenha alguma linha: um bloco cuja unica chave e uma
  // `page_language` inutilizavel nao deixa titulo sozinho.
  const showContext = Boolean(pageUrl !== undefined || client || languages || cart || tenantAttributes.length);

  return (
    // Abaixo de lg a pagina e de um painel so e esta coluna nao existe. Daqui
    // para cima existe quando a concha a manda (o operador abriu-a).
    <aside
      aria-label={translate("conversationsContactPanel")}
      aria-busy={loading || undefined}
      className="w-[20rem] shrink-0 hidden lg:flex flex-col min-h-0 border-l border-[var(--color-border)]"
    >
      {/* O cabecalho nao depende dos dados, portanto desenha-se logo e a regra
          dos 56px atravessa as tres colunas desde o primeiro frame. */}
      <header className={COLUMN_HEADER_CLASS}>
        <h2 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
          {translate("conversationsContactPanel")}
        </h2>
      </header>

      {/* `relative` pela mesma razao da coluna da lista: os rotulos `sr-only`
          das linhas de identidade sao `position: absolute` e sem isto a caixa
          deles ia contar para a raiz do documento. */}
      <div className="relative flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable] px-4 py-3">
        {!conversation ? (
          // As linhas de identidade (email, telefone, identificador) nao se
          // desenham de proposito: 96 em 97 contactos nao as tem, e campos que
          // depois se evaporam prometem dados que nao vem. O "visto pela
          // primeira vez" desenha-se, porque esse tem-no toda a gente, e sem
          // ele o titulo do historico aterrava 57px abaixo do seu esqueleto.
          // Usa o IconRow verdadeiro para as duas geometrias nao poderem
          // divergir outra vez.
          <div role="status" aria-label={translate("loading")}>
            <dl className="space-y-0.5">
              <IconRow icon={CalendarDays} label={translate("conversationsContactFirstSeen")}>
                <Skeleton variant="custom" width="w-40" height="h-3" rounded="rounded" />
              </IconRow>
            </dl>
            <div className={HISTORY_SECTION_CLASS}>
              <div className="h-4 mb-2 flex items-center">
                <Skeleton variant="custom" width="w-28" height="h-3" rounded="rounded" />
              </div>
              <HistoryRowsSkeleton />
            </div>
          </div>
        ) : (
          <>
            {contact && (
              // Os factos que ha. Nao ha linhas "sem dados": um campo que o
              // contacto nao tem nao existe. O "visto pela primeira vez" fica
              // para todos, anonimos incluidos, porque para 96 em 97 contactos
              // e o unico facto que existe, e "visto ha tres semanas" ao lado
              // de quatro conversas anteriores conta uma historia.
              <dl className="space-y-0.5">
                {contact.email && (
                  <IconRow icon={Mail} label={translate("conversationsContactEmail")} title={contact.email}>
                    <MaybeLink href={mailtoHref(contact.email)}>{contact.email}</MaybeLink>
                  </IconRow>
                )}
                {contact.phone && (
                  <IconRow icon={Phone} label={translate("conversationsContactPhone")} title={contact.phone}>
                    <MaybeLink href={telHref(contact.phone)}>{contact.phone}</MaybeLink>
                  </IconRow>
                )}
                {contact.identifier && (
                  <IconRow icon={Hash} label={translate("conversationsContactIdentifier")} title={contact.identifier}>
                    {contact.identifier}
                  </IconRow>
                )}
                {contact.created_at && (
                  <IconRow
                    icon={CalendarDays}
                    label={translate("conversationsContactFirstSeen")}
                    title={formatAbsolute(contact.created_at, language)}
                  >
                    {`${translate("conversationsContactFirstSeen")} ${formatRelative(contact.created_at, language)}`}
                  </IconRow>
                )}
              </dl>
            )}

            {showContext && (
              <div className={contact ? "mt-3 pt-3 border-t border-[var(--color-border)]" : ""}>
                {/* O titulo diz de onde vem: estes atributos sao o que o
                    browser do cliente mandou naquele turno, nao propriedades
                    guardadas no contacto. */}
                <SectionTitle>{translate("conversationsContextHeading")}</SectionTitle>
                <dl className="space-y-2">
                  {pageUrl !== undefined &&
                    (page ? (
                      <IconRow icon={Globe} label={translate("conversationsContextPage")} title={pageUrl} wrap>
                        <a href={page.href} target="_blank" rel="noopener noreferrer" className={LINK_CLASSES}>
                          {clipText(page.path).shown}
                        </a>
                      </IconRow>
                    ) : (
                      <PairRow label={translate("conversationsContextPage")}>
                        <ContextValue value={pageUrl} translate={translate} />
                      </PairRow>
                    ))}
                  {client && (
                    <IconRow
                      icon={deviceIcon(client)}
                      label={translate("conversationsContextDevice")}
                      title={deviceSummary(client, translate)}
                    >
                      {deviceSummary(client, translate)}
                    </IconRow>
                  )}
                  {languages && (
                    <IconRow icon={Languages} label={translate("conversationsContextLanguages")} title={languages.title} wrap>
                      {languages.lines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </IconRow>
                  )}
                  {cart && <CartRow cart={cart} translate={translate} language={language} />}
                  {tenantAttributes.map(([key, value]) => (
                    <PairRow key={key} label={humanizeKey(key)}>
                      <ContextValue value={value} translate={translate} />
                    </PairRow>
                  ))}
                </dl>
                {(client?.user_agent || client?.ip) && (
                  // So chegam a quem tem acesso total: o backend tira-os aos outros
                  // (spec, decisao 11), portanto o painel desenha o que vier.
                  <div className="mt-2">
                    <TechnicalDetails ip={client.ip} userAgent={client.user_agent} translate={translate} />
                  </div>
                )}
              </div>
            )}

            {customerProfile && (
              <div className={contact || showContext ? "mt-3 pt-3 border-t border-[var(--color-border)]" : ""}>
                <SectionTitle>{translate("conversationsCustomerProfileHeading")}</SectionTitle>
                <dl className="space-y-2">
                  {orderProfileEntries(customerProfile).map(([key, value]) => (
                    <PairRow key={key} label={key === PROFILE_NAME_KEY ? translate("conversationsProfileName") : humanizeKey(key)}>
                      <ContextValue value={value} translate={translate} />
                    </PairRow>
                  ))}
                </dl>
              </div>
            )}

            {contact && (
              <div className={HISTORY_SECTION_CLASS}>
                <SectionTitle>{translate("conversationsContactHistory")}</SectionTitle>
                {historyFailed ? (
                  // Uma leitura que falhou nao pode passar por "este contacto nao
                  // tem historico", tal como a lista e a transcricao ja dizem que
                  // nao conseguiram em vez de dizerem que nao ha nada.
                  <p className="text-[13px]" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("conversationsContactHistoryFailed")}
                  </p>
                ) : !history ? (
                  <div role="status" aria-busy="true" aria-label={translate("loading")}>
                    <HistoryRowsSkeleton />
                  </div>
                ) : history.rows.length === 0 ? (
                  // Um facto, nao uma ausencia: "sem resultados" leria como uma
                  // falha de pesquisa quando o que ali esta e informacao.
                  <p className="text-[13px]" style={{ color: "var(--color-text-primary)" }}>
                    {translate("conversationsContactFirstConversation")}
                  </p>
                ) : (
                  <>
                    <div className="-mx-2">{history.rows.map(historyRow)}</div>
                    {history.more && (
                      <p className="text-xs mt-2" style={{ color: "var(--color-text-secondary)" }}>
                        {translate("conversationsContactHistoryMore", {
                          count: HISTORY_SHOWN,
                        })}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
};

export default ConversationContactPanel;
